import React from "react";
import {
  FaHourglassHalf,
  FaCheckCircle,
  FaPlay,
  FaFlagCheckered,
  FaMapMarkerAlt,
  FaCar,
  FaBan,
  FaUserSlash,
  FaUserTimes,
} from "react-icons/fa";
import {
  formatMoney,
  formatStatusSharePercent,
  calcAverageFare,
} from "./DriverRideStatsCards";

export const emptyUserStatusBreakdown = () => ({
  totalRequestedRides: 0,
  available: 0,
  accepted: 0,
  arrived: 0,
  started: 0,
  completed: 0,
  cancelled: 0,
  totalRequestedRidesFare: 0,
  availableFare: 0,
  acceptedFare: 0,
  arrivedFare: 0,
  startedFare: 0,
  completedFare: 0,
  cancelledFare: 0,
  cancelledByUser: 0,
  cancelledByUserFare: 0,
  cancelledByDriver: 0,
  cancelledByDriverFare: 0,
  cancelledByUserBeforeAssigned: 0,
  cancelledByUserBeforeAssignedFare: 0,
});

const cardBaseClass =
  "rounded-lg p-3 shadow-md text-white transition-transform duration-150";

export const USER_CARD_FILTER_LABELS = {
  totalRequestedRides: "Total Requested Rides",
  available: "Available",
  accepted: "Accepted",
  arrived: "Arrived",
  started: "Started",
  completed: "Completed",
  cancelled: "Cancelled (All)",
  cancelledByUser: "Cancelled by User",
  cancelledByDriver: "Cancelled by Driver",
  cancelledByUserBeforeAssigned: "Cancelled by User - Before Driver Assigned",
};

const statusRow1 = [
  {
    key: "totalRequestedRides",
    countKey: "totalRequestedRides",
    fareKey: "totalRequestedRidesFare",
    label: "Total Requested Rides",
    gradient: "bg-gradient-to-r from-slate-600 to-slate-700",
    icon: FaCar,
  },
  {
    key: "available",
    countKey: "available",
    fareKey: "availableFare",
    label: "Available",
    gradient: "bg-gradient-to-r from-amber-500 to-amber-600",
    icon: FaHourglassHalf,
  },
  {
    key: "accepted",
    countKey: "accepted",
    fareKey: "acceptedFare",
    label: "Accepted",
    gradient: "bg-gradient-to-r from-blue-500 to-blue-600",
    icon: FaCheckCircle,
  },
  {
    key: "arrived",
    countKey: "arrived",
    fareKey: "arrivedFare",
    label: "Arrived",
    gradient: "bg-gradient-to-r from-teal-500 to-teal-600",
    icon: FaMapMarkerAlt,
  },
];

const statusRow2 = [
  {
    key: "started",
    countKey: "started",
    fareKey: "startedFare",
    label: "Started",
    gradient: "bg-gradient-to-r from-cyan-500 to-cyan-600",
    icon: FaPlay,
  },
  {
    key: "completed",
    countKey: "completed",
    fareKey: "completedFare",
    label: "Completed",
    gradient: "bg-gradient-to-r from-green-500 to-green-600",
    icon: FaFlagCheckered,
  },
  {
    key: "cancelled",
    countKey: "cancelled",
    fareKey: "cancelledFare",
    label: "Cancelled (All)",
    gradient: "bg-gradient-to-r from-gray-500 to-gray-600",
    icon: FaBan,
  },
  {
    key: "cancelledByUser",
    countKey: "cancelledByUser",
    fareKey: "cancelledByUserFare",
    label: "Cancelled by User",
    gradient: "bg-gradient-to-r from-rose-500 to-rose-600",
    icon: FaUserTimes,
  },
  {
    key: "cancelledByDriver",
    countKey: "cancelledByDriver",
    fareKey: "cancelledByDriverFare",
    label: "Cancelled by Driver",
    gradient: "bg-gradient-to-r from-orange-500 to-orange-600",
    icon: FaUserSlash,
  },
  {
    key: "cancelledByUserBeforeAssigned",
    countKey: "cancelledByUserBeforeAssigned",
    fareKey: "cancelledByUserBeforeAssignedFare",
    label: "Cancelled by User - Before Driver Assigned",
    gradient: "bg-gradient-to-r from-pink-500 to-pink-600",
    icon: FaUserTimes,
  },
];

const RideStatCard = ({
  cfg,
  stats,
  totalRidesCount,
  loading,
  onCardClick,
  selectedCardFilter,
}) => {
  const Icon = cfg.icon;
  const count = Number(stats[cfg.countKey]) || 0;
  const tripAmount = Number(stats[cfg.fareKey]) || 0;
  const avgFare = calcAverageFare(count, tripAmount);
  const showShare = cfg.key !== "totalRequestedRides";
  const canClick = typeof onCardClick === "function";
  const isActive =
    selectedCardFilter === cfg.key ||
    (cfg.key === "totalRequestedRides" && !selectedCardFilter);
  const classes = `${cardBaseClass} ${cfg.gradient} ${
    canClick
      ? "cursor-pointer hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-white/70"
      : ""
  } ${isActive ? "ring-4 ring-offset-2 ring-offset-white ring-slate-800 scale-[1.02]" : ""}`;

  const handleClick = () => {
    if (!canClick) return;
    onCardClick(cfg.key);
  };

  const content = (
    <>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-wider opacity-90 truncate pr-2">
          {cfg.label}
        </span>
        <Icon className="text-base opacity-80 flex-shrink-0" />
      </div>
      <div className="text-lg md:text-xl font-bold leading-tight">
        {loading ? (
          <span className="inline-block h-6 w-20 rounded bg-white/30 animate-pulse" />
        ) : (
          <>
            {count.toLocaleString("en-IN")}
            {showShare && (
              <span className="text-sm font-semibold opacity-90 ml-1">
                ({formatStatusSharePercent(count, totalRidesCount)}%)
              </span>
            )}
          </>
        )}
      </div>
      <div className="mt-1.5 pt-1.5 border-t border-white/30">
        <div className="text-xs font-semibold opacity-95">
          {loading ? (
            <span className="inline-block h-4 w-28 rounded bg-white/30 animate-pulse" />
          ) : (
            <>
              {formatMoney(tripAmount)}
              <span className="text-[10px] font-medium opacity-90 ml-1">
                ({formatMoney(avgFare)} avg)
              </span>
            </>
          )}
        </div>
      </div>
    </>
  );

  if (!canClick) {
    return <div className={classes}>{content}</div>;
  }

  return (
    <button
      type="button"
      className={`${classes} text-left w-full`}
      title={cfg.label}
      onClick={handleClick}
      aria-label={`Filter rides by ${cfg.label}`}
      aria-pressed={isActive}
    >
      {content}
    </button>
  );
};

const UserRideStatsCards = ({
  stats,
  loading = false,
  selectedCardFilter = null,
  onCardClick,
}) => {
  const breakdown = stats || emptyUserStatusBreakdown();
  const totalRidesCount =
    Number(breakdown.totalRequestedRides) ||
    [...statusRow1, ...statusRow2].reduce(
      (sum, cfg) => sum + (Number(breakdown[cfg.countKey]) || 0),
      0
    );

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
          Ride Statistics
        </h3>
        <p className="text-[11px] text-gray-500 mb-2">
          Click a card to filter the rides list below.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          {statusRow1.map((cfg) => (
            <RideStatCard
              key={cfg.key}
              cfg={cfg}
              stats={breakdown}
              totalRidesCount={totalRidesCount}
              loading={loading}
              onCardClick={onCardClick}
              selectedCardFilter={selectedCardFilter}
            />
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {statusRow2.map((cfg) => (
            <RideStatCard
              key={cfg.key}
              cfg={cfg}
              stats={breakdown}
              totalRidesCount={totalRidesCount}
              loading={loading}
              onCardClick={onCardClick}
              selectedCardFilter={selectedCardFilter}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserRideStatsCards;
